import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Table,
  TableBody,
  InputAdornment,
  CircularProgress,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
// import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { AppDispatch } from '../../../../store';
import SortDropdown, { SortOption } from '../../../../components/SortDropdown';
import CustomPagination from '../../../../components/CustomPagination';
import {
  fetchFacilityWorkerAttendance,
  selectFacilityWorkerAttendanceList,
  selectFacilityWorkerAttendanceCount,
  selectFacilityWorkerAttendanceLoading
} from '../../../../modules/facility';
import {
  Container,
  SearchField,
  StyledTableContainer,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  // ExportButton,
  PaginationFooter
} from './styles';

const SORT_OPTIONS: SortOption[] = [
  { id: 'worker_name-asc', label: 'Name (A - Z)' },
  { id: 'worker_name-desc', label: 'Name (Z - A)' }
];

const WorkerAttendance: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const attendanceList = useSelector(selectFacilityWorkerAttendanceList);
  const totalCount = useSelector(selectFacilityWorkerAttendanceCount);
  const loading = useSelector(selectFacilityWorkerAttendanceLoading);

  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [search, setSearch] = useState('');

  const [currentSort, setCurrentSort] = useState(SORT_OPTIONS[0].id);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('worker_name');

  useEffect(() => {
    dispatch(fetchFacilityWorkerAttendance({
      page,
      pageSize: rowsPerPage,
      search,
      orderBy,
      order
    }));
  }, [dispatch, page, rowsPerPage, search, orderBy, order]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handleSortChange = (newSortId: string) => {
    setCurrentSort(newSortId);
    const [newOrderBy, newOrder] = newSortId.split('-');
    setOrderBy(newOrderBy);
    setOrder(newOrder as 'asc' | 'desc');
    setPage(1);
  };

  return (
    <Box sx={{ width: '100%', mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>

        {/* <ExportButton startIcon={<FileDownloadOutlinedIcon fontSize="small" />}>
          Export as CSV/PDF
        </ExportButton> */}
      </Box>

      <Container sx={{ mt: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <SearchField
            placeholder="Search by Worker Name, Position"
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {search && (
                    <IconButton
                      aria-label="clear search"
                      onClick={() => {
                        setSearch('');
                        setPage(1);
                      }}
                      edge="end"
                      size="small"
                      sx={{ mr: 0.5 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            size="small"
          />

          <SortDropdown
            options={SORT_OPTIONS}
            activeSortId={currentSort}
            onSortChange={handleSortChange}
          />
        </Box>

        <StyledTableContainer>
          <Table>
            <StyledTableHead>
              <StyledTableRow>
                <StyledTableCell>ID No</StyledTableCell>
                <StyledTableCell>Worker's Name</StyledTableCell>
                <StyledTableCell>Position</StyledTableCell>
                <StyledTableCell>Total Shifts</StyledTableCell>
                <StyledTableCell>Total Hours</StyledTableCell>
                <StyledTableCell>No shows</StyledTableCell>
                <StyledTableCell>Late</StyledTableCell>
                <StyledTableCell>Attendance %</StyledTableCell>
                <StyledTableCell>Remarks</StyledTableCell>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {loading ? (
                <StyledTableRow>
                  <StyledTableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={30} />
                  </StyledTableCell>
                </StyledTableRow>
              ) : attendanceList.length === 0 ? (
                <StyledTableRow>
                  <StyledTableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    No worker attendance records found
                  </StyledTableCell>
                </StyledTableRow>
              ) : (
                attendanceList.map((worker) => (
                  <StyledTableRow key={worker.worker_id}>
                    <StyledTableCell>{worker.worker_id}</StyledTableCell>
                    <StyledTableCell>{worker.worker_name}</StyledTableCell>
                    <StyledTableCell>{worker.position || '--'}</StyledTableCell>
                    <StyledTableCell>{worker.total_shifts}</StyledTableCell>
                    <StyledTableCell>{Math.abs(Number(worker.total_hours)).toFixed(1)}</StyledTableCell>
                    <StyledTableCell>{worker.no_shows}</StyledTableCell>
                    <StyledTableCell>{worker.late}</StyledTableCell>
                    <StyledTableCell>{worker.attendance_percentage}%</StyledTableCell>
                    <StyledTableCell>{worker.remarks}</StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>

        <PaginationFooter>
          <Box>
            Showing {attendanceList.length} of {totalCount} results
          </Box>
          <CustomPagination
            totalPages={Math.ceil(totalCount / rowsPerPage)}
            currentPage={page}
            onPageChange={(newPage) => setPage(newPage)}
          />
        </PaginationFooter>
      </Container>
    </Box>
  );
};

export default WorkerAttendance;
